// Single source of truth for the "Generative Model" display label.
//
// Two surfaces show which generative model produced (or will produce) a part,
// and they MUST agree:
//   1. The FeatureTreeSidebar "Generative Model" dropdown (live, pre-generation).
//   2. The chat-bubble footer (AssistantMessageFooter, post-generation).
//
// Both call `generativeModelLabel` so the label can never drift between them.
//
// Precedence (highest first):
//   SUPIN          → "NASA SUPIN"          (Fortran solver, no LLM)
//   outputFormat   → 'unitcell'  → "NASA Unitcell Engine"
//                    'implicit'  → "Implicit Voxel LCEM"   (also covers the
//                                  legacy server-side 'proprietary' sentinel)
//   otherwise      → the PARAMETRIC_MODELS friendly name (fallback: raw id)
//
// IMPORTANT: this is the GENERATIVE-MODEL label, which is a different axis from
// the PHYSICS-KERNEL label. The NASA Unitcell Lattice Converter routes through
// the unitcell edge fn (outputFormat 'unitcell') so its generative-model label
// is "NASA Unitcell Engine", but it actually computes on the PicoGK implicit
// voxel kernel — the kernel label is handled separately (see kernelLabels.ts
// and the OutputFormatSelector `displayLabel` override).

import { Model } from '@shared/types';
import { PARAMETRIC_MODELS } from '@/lib/utils';

export type GenerativeModelLabelInput = {
  // The conversation's locked kernel ('unitcell' | 'implicit' | …).
  outputFormat?: 'mesh' | 'solidbody' | 'unitcell' | 'implicit';
  // SUPIN can be detected from either the conversation settings or the
  // message's own artifact (older conversations may not have the settings
  // field populated, so the artifact flag is the reliable per-message signal).
  vehicleSubtype?: string | null;
  hasSupinArtifact?: boolean;
  // The raw model id stored on the message/conversation. May be the
  // server-side 'proprietary' sentinel for non-LLM kernel runs.
  model?: Model | null;
};

export function generativeModelLabel({
  outputFormat,
  vehicleSubtype,
  hasSupinArtifact,
  model,
}: GenerativeModelLabelInput): string {
  // SUPIN takes precedence — its geometry comes from the Fortran solver, and
  // its outputFormat is locked to 'solidbody', so it must be matched before
  // any outputFormat branch.
  if (hasSupinArtifact || vehicleSubtype === 'nasa-supersonic-inlet') {
    return 'NASA SUPIN';
  }

  // Kernel-driven generative paths. The unitcell branch (NASA Lattice
  // Primitives + the Lattice Converter) and the implicit branch both run
  // server-side templates with no LLM in the loop.
  if (outputFormat === 'unitcell') return 'NASA Unitcell Engine';
  if (outputFormat === 'implicit' || model === 'proprietary') {
    return 'Implicit Voxel LCEM';
  }

  // Normal LLM path — map the raw id to its friendly name.
  if (!model) return 'Telekinesis';
  return PARAMETRIC_MODELS.find((m) => m.id === model)?.name ?? model;
}
